import { DynamicDictService } from './shared/services/dynamic-dict.service';
import { JsonDynamicComponentService } from './json-dynamic-component.service';
import { FormControlValueChangedEvent as FormControlValueChangedEvent } from './models/form-value-changed-event';
import { CommonUtilsService } from './shared/common-utils.service';
import { UiControlType } from './enums/ui-control-type.enum';
import {
  Config,
  UIControlValidators,
  UiConfig,
  ConfigDisplayType,
} from './models/ui-config';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  OnDestroy,
  Optional,
  AfterViewInit,
  TemplateRef,
} from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  FormArray,
  FormControl,
  FormGroup,
  ValidatorFn,
} from '@angular/forms';
import {
  forOwn,
  get,
  groupBy,
  isArray,
  isEmpty,
  isEqual,
  isObject,
  keys,
  map,
  forEach,
  some,
  cloneDeep,
} from 'lodash';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  JsonDynamicFormToken,
  EXTERNAL_CONFIG,
} from './json-dynamic-form-token';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Source } from './enums/source.enum';
import { FileStatsEvent } from './models/file-stats';

export interface DependentControl {
  conditionExpression: string;
  formGroup: FormGroup;
  control: FormControl | FormArray | FormGroup;
  config: Config;
}

@Component({
  selector: 'ra-json-dynamic-form',
  templateUrl: './json-dynamic-form.component.html',
  styleUrls: ['./json-dynamic-form.component.scss'],
  providers: [
    {
      provide: JsonDynamicFormToken,
      useExisting: JsonDynamicFormComponent,
    },
    DynamicDictService,
    CommonUtilsService,
  ],
  standalone: false,
})
export class JsonDynamicFormComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() uiConfig: UiConfig;
  @Input() formValue: any;
  @Input() readOnly: boolean = false;
  @Input() validateAllDropdownSelectedValues: boolean;
  @Output() formValueChange = new EventEmitter<any>();
  @Output() configChanged = new EventEmitter<FormGroup>();
  @Output() formControlValueChanged =
    new EventEmitter<FormControlValueChangedEvent>();
  @Output() tabSelectionChanged = new EventEmitter<void>();
  @Input() jsonFormGroup: FormGroup;
  @Input() dynamicDictKey: string;
  // When false, JSON `defaultValue` / `defaultValueConditionExpression` seeds
  // are NOT applied to empty controls. Callers set this false when loading an
  // existing/cloned config so model setup defaults never mutate a saved model
  // (GF-4455); an absent value falls through to the empty-control fallbacks
  // below (checkbox -> false, multi-chips -> [], else null) instead of a seed.
  @Input() applyDefaults: boolean = true;
  // Host-rendered content for a sub-section the JSON config cannot express
  // (e.g. parallel execution), keyed by sub-section name and rendered in
  // the sub-section's body so it keeps the tab's heading and layout.
  @Input() subSectionTemplates: { [subSection: string]: TemplateRef<any> } = {};
  config: Config[];
  displayType = ConfigDisplayType;
  currentDisplayType: ConfigDisplayType;
  keys = keys;
  private subscriptions: Subscription[] = [];
  UIControlType = UiControlType;
  configSections: { [key: string]: Config[] & { isHavingError?: boolean } };
  configMainSections: { [key: string]: Config[] };
  sectionsOrder = [];
  subSections: { [sectionName: string]: string[] } = {};
  dependentControls: DependentControl[] = [];
  dependentValueConfig: Config[] = [];
  propertyPathControlMap: {
    [path: string]: {
      control: AbstractControl;
      config: Config;
    };
  } = {};
  resetControlMap: {
    [pathToChangeValue: string]: {
      [pathToResetControl: string]: string;
    };
  } = {};
  private isMultiSelectExpressions = new WeakMap<Config, string>();
  private fileSelectSubject = new BehaviorSubject<any>(null);
  fileSelect$ = this.fileSelectSubject.asObservable();
  fileSelectObject: FileStatsEvent;
  selectedFileName: string;
  static isConditionSatisfiedMap: { [key: string]: boolean } = {};
  private static readonly SINGLE_EXPR_RE = /^\s*\{\{[\s\S]+\}\}\s*$/;

  constructor(
    @Optional() @Inject(EXTERNAL_CONFIG) private externalConfig: any,
    private jsonDynamicComponentService: JsonDynamicComponentService,
    private dynamicDictService: DynamicDictService,
    private commonUtils: CommonUtilsService,
  ) {
    this.commonUtils.externalConfig = this.externalConfig || {};
  }

  ngOnInit() {
    this.registerFormControlValueChangedSubscription();
    this.currentDisplayType = this.uiConfig.displayType;
    this.config = cloneDeep(this.uiConfig.config);
    this.createDynamicForm(this.config);
    this.dynamicDictService.key = this.dynamicDictKey;
    this.addStylesElementToDom();
  }

  ngAfterViewInit(): void {
    if (this.validateAllDropdownSelectedValues) {
      const configToUpdate = (config: Config) => {
        return config.valueKeyPath
          ? config?.valueKeyPath === 'list_of_columns'
          : false;
      };
      this.jsonDynamicComponentService
        .validateSelectedValuesForAllDropdowns(configToUpdate)
        .then(() => {
          return this.jsonDynamicComponentService.updateAllowedColumnsForAllCsvs();
        })
        .then(() => {
          return;
        });
    }
  }

  handleFileSelectEvent(event: FileStatsEvent) {
    this.fileSelectObject = event;
    this.fileSelectSubject.next(event);
  }

  private addStylesElementToDom() {
    if (this.uiConfig.styles) {
      const styleElement = document.createElement('style');
      styleElement.innerText = this.uiConfig.styles;
      document.querySelector('head').appendChild(styleElement);
    }
    document.querySelector('.ui-disabled')?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true,
    );
  }

  private registerFormControlValueChangedSubscription() {
    this.subscriptions[this.subscriptions.length] =
      this.jsonDynamicComponentService.formValueChanged$.subscribe(
        (event: FormControlValueChangedEvent) => {
          this.formControlValueChanged.emit(event);
          this.resetControlsOnValueChange(event);
          this.addOrRemoveDependentControls();
          this.resolveDynamicConfigProperties();
        },
      );
  }

  createDynamicForm(config: Config[]) {
    if (!this.jsonFormGroup) {
      this.jsonFormGroup = new FormGroup({});
    }
    this.createFormControls(
      this.jsonFormGroup,
      config,
      this.formValue,
      this.dynamicDictKey ? [this.dynamicDictKey] : [],
    );
    this.configMainSections = groupBy(config, 'mainSection');
    this.configSections = groupBy(
      config.filter((config_) => !('mainSection' in config_)),
      'section',
    );
    const configSectionsKeys = keys(this.configSections);
    const sectionsOrder =
      Array.from(
        new Set([
          ...(this.uiConfig.sectionsOrder || []),
          ...configSectionsKeys,
        ]),
      ) || [];
    const subSections = this.uiConfig.subSections;
    this.subSections = subSections;
    const undefinedIndex = sectionsOrder.indexOf('undefined');
    if (undefinedIndex > -1) {
      sectionsOrder.splice(undefinedIndex, 1);
      sectionsOrder.unshift('undefined');
    }
    this.sectionsOrder = sectionsOrder;
    this.subscriptions[this.subscriptions.length] =
      this.jsonFormGroup.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe((_x) => {
          this.configChanged.emit(this.jsonFormGroup);
          this.formValueChange.emit(this.jsonFormGroup.value);
        });
    this.addOrRemoveDependentControls();
    this.resolveDynamicConfigProperties();
  }

  validateControlValues() {
    let isValueReset = false;
    this.dependentValueConfig.forEach((config) => {
      if (config.dependentValues && config.sourceExpression) {
        try {
          this.commonUtils.getDropdownValues(config, this.jsonFormGroup.value);
          this.addOrRemoveDependentControls();
          const currentControl = config.control as FormControl;
          const currentValue = currentControl.value;
          if (
            currentValue != null &&
            !some(config.values, (val) =>
              config.dropdownID
                ? val[config.dropdownID] === currentValue
                : val === currentValue,
            )
          ) {
            currentControl.setValue(null);
            isValueReset = true;
          }
        } catch (_e) {}
      }
    });
    if (isValueReset) {
      this.onValueChanged();
    }
  }

  onValueChanged() {
    this.addOrRemoveDependentControls();
    this.validateControlValues();
  }

  onTabSelectionChanged(): void {
    this.tabSelectionChanged.emit();
  }

  private resetControlsOnValueChange(event: FormControlValueChangedEvent) {
    const changeValuePath = event?.config?.path;
    const resetControlMap = this.resetControlMap[changeValuePath];
    if (resetControlMap) {
      for (let pathToResetControl in resetControlMap) {
        const conditionExpression = resetControlMap[pathToResetControl];
        if (
          (conditionExpression &&
            this.commonUtils.safeEvaluate(
              this.commonUtils.getInterpolatedValue(
                conditionExpression,
                this.jsonFormGroup.value,
              ),
            )) ||
          !conditionExpression
        ) {
          this.propertyPathControlMap?.[pathToResetControl]?.control?.reset();
          const config =
            this.propertyPathControlMap?.[pathToResetControl]?.config;
          this.removeValuesForUrlSource(config);
        }
      }
    }
  }

  private removeValuesForUrlSource(config: Config) {
    if (config.children) {
      config.children.forEach((childConfig) =>
        this.removeValuesForUrlSource(childConfig),
      );
    } else if (config.source === Source.Url || config.reloadValues) {
      config.values = null;
    }
  }

  createFormControls(
    formGroup: FormGroup,
    config: Config[],
    formValue: any,
    parentProperties: string[] = [],
  ) {
    for (let i = 0; i < config.length; i++) {
      const isAlreadyPresent = this.dependentControls.find(
        (control) => control.config === config[i],
      );
      const currentConfig = !isAlreadyPresent
        ? config[i]
        : cloneDeep(config[i]);
      const properties = [...parentProperties, currentConfig.property];
      const path = properties.join('.');
      if (
        ![this.UIControlType.CSV, this.UIControlType.CSVUtils].includes(
          currentConfig.uiControlType,
        ) &&
        currentConfig.children
      ) {
        const newFormGroup =
          (formGroup.get(currentConfig.property) as FormGroup) ||
          new FormGroup({}, this.getValidators(currentConfig.parentValidators));
        if (currentConfig.isDependentControl && currentConfig.property) {
          this.dependentControls.push({
            conditionExpression: currentConfig.conditionExpression,
            formGroup: formGroup,
            control: newFormGroup,
            config: currentConfig,
          });
        } else {
          formGroup.addControl(currentConfig.property, newFormGroup);
        }
        currentConfig.control = newFormGroup;
        this.propertyPathControlMap[path] = {
          control: newFormGroup,
          config: currentConfig,
        };
        this.createFormControls(
          newFormGroup,
          currentConfig.children,
          this.formValue,
          properties,
        );
      } else {
        let newFormControl: any;
        if (formGroup.get(currentConfig.property)) {
          newFormControl = formGroup.get(currentConfig.property);
        } else if (
          currentConfig.uiControlType === this.UIControlType.DynamicList
        ) {
          newFormControl = this.getNewFormArray(
            currentConfig,
            formValue,
            parentProperties,
          );
        } else if (
          currentConfig.uiControlType === this.UIControlType.DynamicDict
        ) {
          newFormControl = this.getNewFormGroupControl(
            currentConfig,
            formValue,
            parentProperties,
          );
        } else {
          newFormControl = this.getNewFormControl(
            currentConfig,
            formValue,
            parentProperties,
          );
        }
        if (currentConfig.source === Source.Dependent) {
          this.dependentValueConfig.push(currentConfig);
        }
        if (!currentConfig.isDependentControl && currentConfig.property) {
          if (
            ![
              this.UIControlType.CSV,
              this.UIControlType.CSVUtils,
              this.UIControlType.Label,
            ].includes(currentConfig.uiControlType)
          ) {
            formGroup.addControl(currentConfig.property, newFormControl);
            currentConfig.control = newFormControl;
          }
        } else {
          this.dependentControls.push({
            conditionExpression: currentConfig.conditionExpression,
            formGroup: formGroup,
            control: newFormControl,
            config: currentConfig,
          });
        }
        this.propertyPathControlMap[path] = {
          control: newFormControl,
          config: currentConfig,
        };
      }
      currentConfig.path = path;
      if (currentConfig.resetOnValueChange) {
        for (let propertyPath in currentConfig.resetOnValueChange) {
          if (!this.resetControlMap[propertyPath]) {
            this.resetControlMap[propertyPath] = {};
          }
          this.resetControlMap[propertyPath][path] =
            currentConfig?.resetOnValueChange?.[
              propertyPath
            ]?.conditionExpression;
        }
      }
    }
  }

  getNewFormControl(
    currentConfig: Config,
    formValue: any,
    parentProperties: string[] = [],
  ) {
    parentProperties = currentConfig.property
      ? [...parentProperties, currentConfig.property]
      : parentProperties;
    const value = !formValue
      ? null
      : get(
          formValue,
          parentProperties,
          get(formValue, [currentConfig.property]),
        );
    const propertyValue =
      !!this.getBooleanDropdownValue(value, currentConfig) ||
      this.getPropertyValue(value, currentConfig);
    return new FormControl(
      propertyValue,
      this.getValidators(currentConfig.validators),
    );
  }

  getBooleanDropdownValue(formValue: any, config: Config) {
    let result;
    if (!formValue) {
      return result;
    }
    if (config?.uiControlType === UiControlType.BooleanDropdown) {
      if (!config?.values?.length) {
        this.commonUtils.getDropdownValues(config, this.jsonFormGroup.value);
      }
      const property = config?.dropdownID;
      const values = property ? map(config.values, property) : config.values;
      for (let i = 0; i < values.length; i++) {
        const value = values[i];
        if (formValue?.[value]) {
          result = value;
          config.property = value;
          break;
        }
      }
    }
    return result;
  }

  getNewFormGroupControl(
    currentConfig: Config,
    formValue: any,
    parentProperties: string[] = [],
  ) {
    parentProperties = [...parentProperties, currentConfig.property];
    const value = !formValue ? null : get(formValue, parentProperties);
    const newFormGroup = new FormGroup({});
    if (currentConfig?.validators?.required) {
      const validatorFunction: ValidatorFn = (control: AbstractControl) => {
        if (isEmpty(control?.value)) {
          return {
            required:
              (currentConfig.displayText || currentConfig.property) +
              ' is required.',
          };
        }
      };
      newFormGroup.addValidators([validatorFunction]);
    }
    if (value == null) {
      if (!!currentConfig?.hasDefaultDictValues) {
        const keyValue =
          !!this.getBooleanDropdownValue(value, currentConfig?.dictKeyConfig) ||
          this.getPropertyValue(value, currentConfig?.dictKeyConfig);
        if (currentConfig?.dictValuesConfig?.valueDefault != null) {
          newFormGroup.addControl(
            keyValue,
            new FormControl(currentConfig?.dictValuesConfig?.valueDefault),
          );
        } else if (currentConfig?.dictValuesConfig?.valueConfig != null) {
          newFormGroup.addControl(
            keyValue,
            this.getNewFormControl(
              currentConfig?.dictValuesConfig?.valueConfig,
              formValue,
              [keyValue],
            ),
          );
        } else {
          const tempFormGroup = new FormGroup({});
          newFormGroup.addControl(keyValue, tempFormGroup);
          this.createFormControls(
            tempFormGroup,
            currentConfig?.dictValuesConfig?.config,
            formValue,
            [keyValue],
          );
        }
      }
      return newFormGroup;
    } else {
      const isValue = currentConfig?.dictValuesConfig?.valueDefault != null;
      const config = currentConfig?.dictValuesConfig?.config;
      const isValueConfig =
        currentConfig?.dictValuesConfig?.valueConfig != null;
      forOwn(value, (val, key) => {
        if (isValue) {
          newFormGroup.addControl(key, new FormControl(val));
        } else if (config) {
          const tempFormGroup = new FormGroup({});
          let propToPass = [key];
          if (
            typeof val === 'object' &&
            config.length === 1 &&
            config[0].property
          ) {
            propToPass = [];
          }
          newFormGroup.addControl(key, tempFormGroup);
          this.createFormControls(tempFormGroup, config, val, propToPass);
        } else if (isValueConfig) {
          const tempFormControl = new FormControl(
            val,
            this.getValidators(
              currentConfig?.dictValuesConfig?.valueConfig?.validators,
            ),
          );
          newFormGroup.addControl(key, tempFormControl);
        }
      });
      return newFormGroup;
    }
  }

  getPropertyValue(value: any, config: Config) {
    const property = config.dropdownID;
    const result =
      property && isObject(value) && value[property] != null
        ? value[property]
        : value;
    if (result != null) {
      return result;
    }
    if (
      this.applyDefaults &&
      (config.defaultValue || config.defaultValueConditionExpression)
    ) {
      let defaultValue: any;
      let evaluatedDefaultValue: any;
      if (config.defaultValue) {
        if (typeof config.defaultValue === 'string') {
          try {
            evaluatedDefaultValue = JSON.parse(
              this.commonUtils.getInterpolatedValue(
                config.defaultValue,
                this.jsonFormGroup.value,
              ),
            );
          } catch (_e) {
            evaluatedDefaultValue = null;
          }
        } else if (typeof config.defaultValue === 'boolean') {
          evaluatedDefaultValue = config.defaultValue;
        } else if (Array.isArray(config.defaultValue)) {
          // Preserve intentional duplicates and order (e.g. a constraints
          // list where a function may legitimately appear more than once).
          // Shallow copy so the config's array isn't mutated downstream.
          evaluatedDefaultValue = [...config.defaultValue];
        } else if (typeof config.defaultValue === 'object') {
          evaluatedDefaultValue = cloneDeep(config.defaultValue);
        }
      } else if (config.defaultValueConditionExpression) {
        forOwn(config.defaultValueConditionExpression, (conditionExpr, key) => {
          if (
            this.commonUtils.safeEvaluate(
              this.commonUtils.getInterpolatedValue(
                conditionExpr,
                this.jsonFormGroup.value,
              ),
            )
          ) {
            evaluatedDefaultValue = key;
          }
        });
      }
      defaultValue = evaluatedDefaultValue || null;
      const isMultiValueControl =
        (config.uiControlType === UiControlType.Dropdown &&
          config?.isMultiSelect) ||
        (config.uiControlType === UiControlType.Chips &&
          config?.isMultiSelect !== false);
      return isMultiValueControl && defaultValue
        ? !(defaultValue instanceof Array)
          ? [defaultValue]
          : [...defaultValue]
        : defaultValue;
    }
    if (config.uiControlType === UiControlType.Checkbox) {
      return false;
    }
    if (
      config.uiControlType === UiControlType.Chips &&
      config?.isMultiSelect !== false
    ) {
      return [];
    }
    return null;
  }

  getNewFormArray(
    currentConfig: Config,
    formValue: any,
    parentProperties: string[] = [],
  ) {
    parentProperties = [...parentProperties, currentConfig.property];
    const values: any[] = !formValue
      ? [null]
      : get(
          formValue,
          parentProperties,
          get(formValue, [currentConfig.property]),
        ) || [null];
    const parentValidators = currentConfig.parentValidators;
    const formArray = new FormArray([], this.getValidators(parentValidators));
    if (currentConfig?.validators?.required) {
      const requiredArrayValidator: ValidatorFn = (
        control: AbstractControl,
      ) => {
        const value = control?.value;
        if (!Array.isArray(value) || value.length === 0) {
          return {
            required:
              (currentConfig.displayText || currentConfig.property) +
              ' is required.',
          };
        }
        return null;
      };
      formArray.addValidators([requiredArrayValidator]);
    }
    values.forEach((value) => {
      formArray.push(
        new FormControl(value, this.getValidators(currentConfig.validators)),
      );
    });
    return formArray;
  }

  onInputClick(event: PointerEvent) {
    event.stopPropagation();
    event.stopImmediatePropagation();
    event.preventDefault();
  }

  isConditionSatisfied(config: Config) {
    return (
      JsonDynamicFormComponent.isConditionSatisfiedMap[config.path] || false
    );
  }

  private resolveDynamicConfigProperties() {
    const formValue = this.jsonFormGroup?.value ?? {};
    const seen = new Set<Config>();
    const visit = (config: Config) => {
      if (!config || seen.has(config)) {
        return;
      }
      seen.add(config);
      this.resolveDynamicIsMultiSelect(config, formValue);
    };
    for (const dep of this.dependentControls) {
      visit(dep.config);
    }
    for (const path in this.propertyPathControlMap) {
      visit(this.propertyPathControlMap[path].config);
    }
  }

  /**
   * Returns the full set of allowed option values for a dropdown-style config.
   * Prefers `config.allOptions` (populated by `getDropdownValues` once the
   * control has rendered) and falls back to `config.values` plus an evaluated
   * `valuesToAdd`. Used when re-adding a dependent control so saved values
   * that came from a dynamic `valuesToAdd` expression aren't wiped by an
   * intersection against the stale static `config.values` alone.
   */
  private getAllowedValuesForControl(config: Config): any[] {
    if (Array.isArray(config.allOptions) && config.allOptions.length > 0) {
      return config.allOptions;
    }
    const base = Array.isArray(config.values) ? config.values : [];
    let extra: any[] = [];
    const valuesToAdd = config.valuesToAdd;
    if (Array.isArray(valuesToAdd)) {
      extra = valuesToAdd;
    } else if (typeof valuesToAdd === 'string') {
      try {
        const evald = this.commonUtils.safeEvaluate(
          this.commonUtils.getInterpolatedValue(
            valuesToAdd,
            this.jsonFormGroup?.value ?? {},
          ),
        );
        if (Array.isArray(evald)) {
          extra = evald;
        }
      } catch (_e) {
        extra = [];
      }
    }
    return [...new Set([...base, ...extra])];
  }

  private resolveDynamicIsMultiSelect(config: Config, formValue: any) {
    let expr = this.isMultiSelectExpressions.get(config);
    if (expr === undefined) {
      // Only intercept `{{...}}` expressions; leave legacy `"true"` strings alone.
      if (
        typeof config.isMultiSelect === 'string' &&
        JsonDynamicFormComponent.SINGLE_EXPR_RE.test(config.isMultiSelect)
      ) {
        expr = config.isMultiSelect;
        this.isMultiSelectExpressions.set(config, expr);
      } else {
        return;
      }
    }
    const resolved = this.commonUtils.resolveTypedConfigValue(expr, formValue);
    config.isMultiSelect = !!resolved;
  }

  addOrRemoveDependentControls() {
    const conditionSatisfiedControlsMap = {};
    const controlsToRemove = {};
    this.dependentControls.forEach((dependentControl) => {
      let isConditionSatisfied;
      if (dependentControl.conditionExpression) {
        isConditionSatisfied = this.isConditionExpressionSatisfied(
          dependentControl.conditionExpression,
          dependentControl.formGroup.value,
        );
      }
      JsonDynamicFormComponent.isConditionSatisfiedMap[
        dependentControl.config.path
      ] = isConditionSatisfied;
      dependentControl.config.isConditionSatisfied = isConditionSatisfied;
      if (isConditionSatisfied) {
        if (dependentControl.control && dependentControl?.config?.property) {
          conditionSatisfiedControlsMap[dependentControl.config.path] = {
            dependentControl: dependentControl,
          };
          if (
            !dependentControl?.formGroup?.get(
              dependentControl.config.property,
            ) ||
            (
              dependentControl?.formGroup?.get(
                dependentControl.config.property,
              ) as FormArray
            ).controls?.length === 0
          ) {
            if (
              ![
                this.UIControlType.CSV,
                this.UIControlType.CSVUtils,
                this.UIControlType.Label,
              ].includes(dependentControl.config.uiControlType)
            ) {
              dependentControl.formGroup.addControl(
                dependentControl.config.property,
                dependentControl.control,
              );
            }
            dependentControl.config.control = dependentControl.control;
            if (dependentControl?.config?.values?.length > 0) {
              const dependentControlValue = dependentControl?.control?.value;
              const allowedValues = this.getAllowedValuesForControl(
                dependentControl.config,
              );
              if (
                !isArray(dependentControlValue) &&
                allowedValues.indexOf(dependentControlValue) === -1
              ) {
                dependentControl.control.setValue(null);
              } else if (isArray(dependentControlValue)) {
                // Filter in place to keep saved order and duplicates while
                // pruning stale entries (a set intersection would drop both).
                const intersectedValues = dependentControlValue.filter(
                  (value: unknown) =>
                    allowedValues.some((allowed) => isEqual(allowed, value)),
                );
                dependentControl.control.setValue(intersectedValues);
              }
            }
          }
        }
      } else {
        controlsToRemove[dependentControl.config.path] = {
          dependentControl: dependentControl,
        };
      }
    });

    forOwn(
      controlsToRemove,
      (value: { dependentControl: DependentControl }, path) => {
        const conditonSatisfiedObj = conditionSatisfiedControlsMap[path] as {
          dependentControl: DependentControl;
        };
        if (
          !conditonSatisfiedObj ||
          value?.dependentControl?.formGroup !==
            conditonSatisfiedObj.dependentControl?.formGroup
        ) {
          if (
            value.dependentControl.formGroup?.get(
              value.dependentControl.config.property,
            )
          ) {
            value.dependentControl.formGroup.removeControl(
              value.dependentControl.config.property,
            );
            if (value.dependentControl?.config?.control) {
              value.dependentControl.config.control = null;
            }
          }
        }
      },
    );
  }

  private isConditionExpressionSatisfied(expr: string, formValue: any) {
    const value = expr.includes('dynamicDictKey')
      ? Object.assign({ dynamicDictKey: this.dynamicDictKey })
      : formValue;
    return this.commonUtils.getInterpolatedValue(expr, value) === 'true'
      ? true
      : false;
  }

  getValidators(
    uiControlValidators: UIControlValidators,
  ): ValidatorFn | ValidatorFn[] | AbstractControlOptions {
    return CommonUtilsService.getValidators(uiControlValidators);
  }

  isHavingError(section: string) {
    if (this.configMainSections[section]) {
      let isHavingError = false;
      forEach(this.subSections[section], (subSections) => {
        forEach(this.configSections[subSections], (config) => {
          if (this.isHavingErrorInConfig(config)) {
            isHavingError = true;
            this.configSections[subSections]['isHavingError'] = true;
            return false;
          }
          this.configSections[subSections]['isHavingError'] = false;
        });
      });
      return isHavingError;
    }
    return false;
  }

  private isHavingErrorInConfig(config: Config) {
    let isError = false;
    if (config.children) {
      forEach(config.children, (childConfig) => {
        if (this.isHavingErrorInConfig(childConfig)) {
          isError = true;
          return false;
        }
      });
      return isError;
    }
    if (
      config.dictValuesConfig?.valueConfig &&
      this.isHavingErrorInConfig(config.dictValuesConfig?.valueConfig)
    ) {
      return true;
    }
    if (config.dictValuesConfig?.config) {
      forEach(config.dictValuesConfig?.config, (dictValueConfig) => {
        if (this.isHavingErrorInConfig(dictValueConfig)) {
          isError = true;
          return false;
        }
      });
      return isError;
    }
    if (
      config.control &&
      !(config.control as AbstractControl)?.disabled &&
      (config.control as AbstractControl)?.errors?.['emptyRows']
    ) {
      return true;
    }
    if (
      config.control &&
      !(config.control as AbstractControl)?.valid &&
      (config.control as AbstractControl)?.touched &&
      !(config.control as AbstractControl)?.disabled
    ) {
      return true;
    }
    if (
      config?.csvConfig?.notSavedError ||
      config?.csvConfig?.notSelectedError ||
      config?.csvConfig?.invalidColumnsError
    ) {
      return true;
    }
    return isError;
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
